import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Search, MoreHorizontal, Eye, Edit, Package, PackageCheck, ShieldCheck, XCircle, RotateCcw, Archive, Boxes } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusConfig = {
  RECEIVED: { label: "Received", className: "bg-blue-50 text-blue-700 border-blue-200", icon: Package },
  INSPECTED: { label: "Inspected", className: "bg-amber-50 text-amber-700 border-amber-200", icon: Eye },
  ACCEPTED: { label: "Accepted", className: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ShieldCheck },
  REJECTED: { label: "Rejected", className: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
  RETURNED: { label: "Returned", className: "bg-slate-100 text-slate-600 border-slate-200", icon: RotateCcw },
};

const StatusBadge = ({ status }) => {
  const c = statusConfig[status] || { label: status, className: "bg-slate-50 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.className}`}>
      {c.icon && <c.icon className="w-3 h-3" />}
      {c.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${color.replace('text-', 'bg-').replace('700', '50').replace('900', '50')}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

function GRNList() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    Promise.all([fetchEntries(), fetchStats()]);
  }, []);

  const fetchEntries = async () => {
    try {
      const r = await axios.get(`${API_URL}/grn`, getAuthHeader());
      setEntries(r.data);
    } catch { toast.error("Failed to load GRN entries"); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API_URL}/grn-stats`, getAuthHeader());
      setStats(r.data);
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/grn/${id}/status?status=${status}`, {}, getAuthHeader());
      toast.success(`Status updated to ${status}`);
      fetchEntries();
      fetchStats();
    } catch { toast.error("Failed to update status"); }
  };

  const filtered = entries.filter(e => {
    const matchTab = tab === "all" || e.entry_type === tab;
    const matchSearch = !search ||
      e.grn_number?.toLowerCase().includes(search.toLowerCase()) ||
      e.vendor_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.purchase_order_no?.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  return (
    <div className="space-y-6" data-testid="grn-list-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Goods Received Notes</h1>
          <p className="text-slate-500 mt-1">Track and manage all incoming goods — Assets & Consumables</p>
        </div>
        <Button onClick={() => navigate("/grn/new")} className="bg-slate-900 hover:bg-slate-800" data-testid="new-grn-btn">
          <Plus className="w-4 h-4 mr-2" /> New GRN Entry
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Boxes} label="Total Entries" value={stats.total} color="text-slate-900" />
          <StatCard icon={Archive} label="Assets" value={stats.assets} color="text-blue-700" />
          <StatCard icon={Package} label="Consumables" value={stats.consumables} color="text-violet-700" />
          <StatCard icon={PackageCheck} label="Accepted" value={stats.by_status?.accepted || 0} color="text-emerald-700" />
          <StatCard icon={Boxes} label="Total Value" value={`₹${(stats.total_value || 0).toLocaleString('en-IN')}`} color="text-slate-900" sub="All entries" />
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by GRN number, vendor, or PO..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="grn-search" />
            </div>
            <Tabs value={tab} onValueChange={setTab} className="w-auto">
              <TabsList className="h-9">
                <TabsTrigger value="all" className="text-xs px-3">All</TabsTrigger>
                <TabsTrigger value="ASSET" className="text-xs px-3">Assets</TabsTrigger>
                <TabsTrigger value="CONSUMABLE" className="text-xs px-3">Consumables</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1,2,3,4].map(i => <div key={i} className="flex gap-4"><Skeleton className="h-4 w-28"/><Skeleton className="h-4 flex-1"/><Skeleton className="h-4 w-20"/></div>)}
            </div>
          ) : filtered.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">GRN Number</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Vendor</TableHead>
                  <TableHead className="font-semibold">PO No.</TableHead>
                  <TableHead className="font-semibold">Items</TableHead>
                  <TableHead className="font-semibold">Value</TableHead>
                  <TableHead className="font-semibold">Received</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(e => (
                  <TableRow key={e.id} className="cursor-pointer hover:bg-slate-50/50" data-testid={`grn-row-${e.id}`}>
                    <TableCell className="font-mono font-semibold text-slate-900 text-sm">{e.grn_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={e.entry_type === "ASSET" ? "border-blue-200 text-blue-700 bg-blue-50" : "border-violet-200 text-violet-700 bg-violet-50"}>
                        {e.entry_type === "ASSET" ? "Asset" : "Consumable"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[180px] truncate">{e.vendor_name || "—"}</TableCell>
                    <TableCell className="text-slate-600 font-mono text-sm">{e.purchase_order_no || "—"}</TableCell>
                    <TableCell className="text-slate-700 font-medium">{e.item_count || 0}</TableCell>
                    <TableCell className="font-semibold text-slate-900">₹{(e.total_value || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{e.date_of_receipt ? new Date(e.date_of_receipt).toLocaleDateString('en-IN') : "—"}</TableCell>
                    <TableCell><StatusBadge status={e.status} /></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={ev => ev.stopPropagation()}>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/grn/${e.id}`)}><Eye className="w-4 h-4 mr-2" />View Details</DropdownMenuItem>
                          {!["ACCEPTED", "REJECTED"].includes(e.status) && (
                            <DropdownMenuItem onClick={() => navigate(`/grn/new?edit=${e.id}`)} data-testid={`edit-grn-${e.id}`}><Edit className="w-4 h-4 mr-2" />Edit GRN</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {e.status === "RECEIVED" && <DropdownMenuItem onClick={() => updateStatus(e.id, "INSPECTED")}><Eye className="w-4 h-4 mr-2" />Mark Inspected</DropdownMenuItem>}
                          {(e.status === "RECEIVED" || e.status === "INSPECTED") && <DropdownMenuItem onClick={() => updateStatus(e.id, "ACCEPTED")}><ShieldCheck className="w-4 h-4 mr-2" />Accept</DropdownMenuItem>}
                          {(e.status === "RECEIVED" || e.status === "INSPECTED") && <DropdownMenuItem onClick={() => updateStatus(e.id, "REJECTED")} className="text-rose-600"><XCircle className="w-4 h-4 mr-2" />Reject</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-16 text-center">
              <Package className="w-14 h-14 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No GRN entries found</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">{search ? "Try a different search" : "Create your first Goods Received Note"}</p>
              <Button variant="outline" onClick={() => navigate("/grn/new")}>Create GRN Entry</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default GRNList;
